import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiCheckCircle, FiMapPin } from "react-icons/fi";

import { loadPlacesLibrary, PRIORITY_REGION_CODES } from "src/Common/googleMaps";
import AddressMap from "./addressmap";

const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 250;
const PANEL_MAX_HEIGHT = 280;

/**
 * Address text input with Google Places autocomplete and a small map preview.
 *
 * The value is a plain string: picking a suggestion stores Google's formatted
 * address as-is, and typing by hand stays allowed (new builds, rural
 * addresses). Without a Maps key it behaves like an ordinary input.
 */
export default function AddressInput({ value = "", onChange, placeholder = "Enter address", showMap = true }) {
  const [places, setPlaces] = useState(null);
  // Only what the user typed triggers a search — not prefilled or picked values.
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [pickedAddress, setPickedAddress] = useState(null);
  // Follows the value only on pick/blur so the map doesn't reload per keystroke.
  const [mapAddress, setMapAddress] = useState(value.trim());
  const [panelStyle, setPanelStyle] = useState(null);

  const listboxId = useId();
  const inputRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const requestIdRef = useRef(0);
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    let cancelled = false;
    loadPlacesLibrary().then((library) => {
      if (!cancelled) setPlaces(library);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Edit forms fill the value after mount; keep the map in step when the
  // change didn't come from the user typing.
  useEffect(() => {
    if (document.activeElement !== inputRef.current) setMapAddress(value.trim());
  }, [value]);

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    const input = query.trim();
    if (!places || input.length < MIN_QUERY_LENGTH) {
      setSuggestions([]);
      setOpen(false);
      return undefined;
    }

    const timer = setTimeout(async () => {
      // One token per typing session; fetching the picked place closes it.
      if (!sessionTokenRef.current) sessionTokenRef.current = new places.AutocompleteSessionToken();
      const request = { input, sessionToken: sessionTokenRef.current };

      try {
        let { suggestions: found } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          ...request,
          includedRegionCodes: PRIORITY_REGION_CODES,
        });
        if (!found.length) {
          ({ suggestions: found } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request));
        }
        if (requestId !== requestIdRef.current) return;

        const predictions = found.map((suggestion) => suggestion.placePrediction).filter(Boolean);
        setSuggestions(predictions);
        setActiveIndex(-1);
        setOpen(predictions.length > 0);
      } catch (error) {
        // Quota, network or key problems: drop the suggestions, keep typing working.
        if (requestId !== requestIdRef.current) return;
        setSuggestions([]);
        setOpen(false);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query, places]);

  const updatePosition = useCallback(() => {
    const input = inputRef.current;
    if (!input) return;

    const rect = input.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUp = spaceBelow < PANEL_MAX_HEIGHT && spaceAbove > spaceBelow;
    const maxHeight = Math.min(PANEL_MAX_HEIGHT, Math.max(openUp ? spaceAbove : spaceBelow, 120) - 8);

    setPanelStyle({
      position: "fixed",
      left: rect.left,
      width: rect.width,
      maxHeight,
      ...(openUp ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
    });
  }, []);

  // Portal + fixed position so the panel isn't clipped by the modal's scroll box.
  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePosition();

    const handle = () => updatePosition();
    window.addEventListener("scroll", handle, true);
    window.addEventListener("resize", handle);
    return () => {
      window.removeEventListener("scroll", handle, true);
      window.removeEventListener("resize", handle);
    };
  }, [open, updatePosition]);

  const pick = async (prediction) => {
    const predictionText = prediction.text.text;
    requestIdRef.current += 1;
    setQuery("");
    setSuggestions([]);
    setOpen(false);
    onChange(predictionText);

    let address = predictionText;
    try {
      // The prediction lacks the ZIP / postal code; the place's formatted address has it.
      const place = prediction.toPlace();
      await place.fetchFields({ fields: ["formattedAddress"] });
      address = place.formattedAddress || predictionText;
    } catch (error) {
      // Keep the prediction text.
    } finally {
      sessionTokenRef.current = null;
    }

    // The user may have kept typing while the details loaded.
    if (valueRef.current !== predictionText) return;
    onChange(address);
    setPickedAddress(address);
    setMapAddress(address);
  };

  const handleKeyDown = (event) => {
    if (!open || suggestions.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % suggestions.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => (index <= 0 ? suggestions.length - 1 : index - 1));
    } else if (event.key === "Enter" && activeIndex >= 0) {
      event.preventDefault();
      pick(suggestions[activeIndex]);
    } else if (event.key === "Escape") {
      event.stopPropagation();
      setOpen(false);
    }
  };

  const isVerified = Boolean(pickedAddress) && pickedAddress === value;

  return (
    <div className="flex flex-col gap-2">
      <div className="relative">
        <input
          ref={inputRef}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setQuery(e.target.value);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          onBlur={() => {
            setOpen(false);
            setMapAddress(value.trim());
          }}
          placeholder={places ? "Start typing an address" : placeholder}
          autoComplete="off"
          role="combobox"
          aria-controls={listboxId}
          aria-expanded={open}
          aria-autocomplete="list"
          className={`w-full h-[40px] pl-3 ${
            isVerified ? "pr-9" : "pr-3"
          } rounded-[6px] border border-borderColor text-[14px] leading-[20px] tracking-[0.14px] font-OpenSansRegular text-inputEmailTitle outline-none focus:border-blueColor`}
        />
        {isVerified && (
          <span title="Address found on Google Maps" className="absolute right-3 top-1/2 -translate-y-1/2">
            <FiCheckCircle size={16} className="text-[#1F7A34]" />
          </span>
        )}
      </div>

      {showMap && <AddressMap address={mapAddress} className="h-[140px]" />}

      {open &&
        panelStyle &&
        createPortal(
          <div
            id={listboxId}
            style={panelStyle}
            role="listbox"
            // Keep focus in the input so blur doesn't close the panel before a click lands.
            onMouseDown={(e) => e.preventDefault()}
            className="flex flex-col bg-white border border-borderColor rounded-[8px] shadow-lg z-[1000]"
          >
            <div className="overflow-y-auto min-h-0 py-1">
              {suggestions.map((prediction, index) => (
                <div
                  key={prediction.placeId}
                  role="option"
                  aria-selected={index === activeIndex}
                  onClick={() => pick(prediction)}
                  onMouseEnter={() => setActiveIndex(index)}
                  className={`flex items-start gap-2 px-3 py-2 cursor-pointer ${
                    index === activeIndex ? "bg-backgroundColor" : ""
                  }`}
                >
                  <FiMapPin size={14} className="mt-[3px] shrink-0 text-subTitleColor" />
                  <div className="min-w-0 flex flex-col">
                    <span className="truncate text-[13px] leading-[20px] font-OpenSansRegular text-titleColor">
                      <MatchedText text={prediction.mainText || prediction.text} />
                    </span>
                    {prediction.mainText && prediction.secondaryText?.text && (
                      <span className="truncate text-[12px] leading-[18px] font-OpenSansRegular text-subTitleColor">
                        {prediction.secondaryText.text}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {/* Google requires attribution when Places results appear without a map:
                12–16px, #5E5E5E, never translated or wrapped. */}
            <div className="px-3 py-1.5 border-t border-borderColor text-right">
              <span
                translate="no"
                className="whitespace-nowrap text-[12px] leading-[16px] font-OpenSansRegular text-[#5E5E5E]"
              >
                Google Maps
              </span>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

/** Renders Places `FormattableText`, bolding the parts that matched the query. */
function MatchedText({ text }) {
  if (!text?.text) return null;

  const parts = [];
  let cursor = 0;
  (text.matches || []).forEach(({ startOffset, endOffset }) => {
    if (startOffset > cursor) parts.push({ text: text.text.slice(cursor, startOffset), matched: false });
    parts.push({ text: text.text.slice(startOffset, endOffset), matched: true });
    cursor = endOffset;
  });
  if (cursor < text.text.length) parts.push({ text: text.text.slice(cursor), matched: false });

  return parts.map((part, index) => (
    <span key={index} className={part.matched ? "font-OpenSansSemiBold" : undefined}>
      {part.text}
    </span>
  ));
}
